/**
 * DIE FLAECHE DES GUIDE — Rennbahn, Griffe, Coach-Spalte, "Gerade in Arbeit".
 *
 * Rafael, 06.09.2026, an einem Entwurf abgenommen ("ja, das ging super"):
 * "Ich will das ganze CUI nicht mehr bedienen. Der Guide bedient mir das CUI."
 *
 * Aufteilung, von ihm festgelegt:
 *   ┌──────────────────────────────────────────────┐
 *   │ Rennbahn — die Strecke des Tages             │
 *   ├───────────────┬───┬──────────┬───┬───────────┤
 *   │ Guide         │ ‖ │ Coach    │ ‖ │ Fenster   │
 *   │ (Themen)      │ ‖ │ (Chat)   │ ‖ │ des       │
 *   ├───────────────┤ ‖ │          │ ‖ │ Themas    │
 *   │ Grundlage     │ ‖ │          │ ‖ │           │
 *   └───────────────┴───┴──────────┴───┴───────────┘
 *
 * WAS HIER NICHT PASSIERT: der Arbeitsbereich rechts wird nicht angefasst. Er
 * bleibt der bestehende Container aus App.tsx — nur schmaler. Panels, die dort
 * haengen (webviews, Terminals), duerfen weder neu mounten noch auf Breite 0
 * fallen; beides haette laufende Arbeit anderer gekostet.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import CuiLitePanel from './panels/CuiLitePanel';
import BrowserPanel from './panels/BrowserPanel';
import { ADRESSZEILE_EREIGNIS, OEFFNEN_EREIGNIS } from '../lib/buehnenAdresszeile';
import { useLiveChannel } from '../hooks/useLiveChannel';

import { startPanelResize } from '../utils/panelResize';
import { GUIDE_HAUS_SCHALTER, GUIDE_HAUSFILTER_PFAD, guideSchalterSchluessel } from '../../shared/guide-hausfilter';

const API = '/api';

/**
 * Ein Griff zwischen zwei Bereichen.
 *
 * Die Position wird GEMERKT. Ein Griff, den Rafael bei jedem Neuladen wieder
 * hinschieben muss, ist keine Einstellung, sondern eine taegliche Aufgabe.
 */
export function Griff({ richtung, wert, setzWert, min, max, merker, hidden = false, className, mindestPx }: {
  richtung: 'senkrecht' | 'waagrecht';
  wert: number;
  setzWert: (v: number) => void;
  min: number;
  max: number;
  merker: string;
  hidden?: boolean;
  className?: string;
  /**
   * Untergrenze in Pixeln, die das Layout selbst haelt (Guide-Spalte: 420 px). Unter ihr
   * bewegt sich die Spalte nicht mehr; der Griff setzt dann den kleinsten Prozentwert,
   * statt zwischen min und der Pixelgrenze unsichtbare Zwischenwerte zu speichern.
   */
  mindestPx?: number;
}) {
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => { stop.current?.(); }, []);
  const ziehen = useCallback((start: React.PointerEvent<HTMLDivElement>) => {
    if (start.button !== 0 || !start.isPrimary) return;
    start.preventDefault();
    stop.current?.();
    const eltern = start.currentTarget.parentElement;
    if (!eltern) return;
    stop.current = startPanelResize(start.currentTarget, start.pointerId, richtung, (e) => {
      const r = eltern.getBoundingClientRect();
      const size = richtung === 'senkrecht' ? r.width : r.height;
      if (size <= 0) return;
      const offset = richtung === 'senkrecht' ? e.clientX - r.left : e.clientY - r.top;
      const v = mindestPx && offset < mindestPx ? min : Math.max(min, Math.min(max, offset / size * 100));
      setzWert(v);
      try { localStorage.setItem(merker, String(v)); } catch { /* Resizing still works without storage. */ }
    });
  }, [richtung, min, max, setzWert, merker, mindestPx]);

  return (
    <div
      className={className} data-ai-id={`guide-griff-${merker}`}
      onPointerDown={ziehen}
      title="Ziehen zum Verschieben"
      style={{
        display: hidden ? 'none' : undefined,
        flex: '0 0 6px',
        touchAction: 'none',
        cursor: richtung === 'senkrecht' ? 'col-resize' : 'row-resize',
        background: 'var(--tn-border)',
      }}
    />
  );
}

/** Gemerkte Griffposition; faellt auf die Vorgabe zurueck, wenn nichts Brauchbares dasteht. */
export function useGriff(merker: string, vorgabe: number): [number, (v: number) => void] {
  const [wert, setzWert] = useState(() => {
    const v = Number(localStorage.getItem(merker));
    return Number.isFinite(v) && v > 5 && v < 95 ? v : vorgabe;
  });
  return [wert, setzWert];
}

/**
 * WIEDERERKENNUNG EINES ZUKLAPPERS ueber den Austausch der HTML hinweg.
 *
 * Die Flaeche wird alle 30 s komplett neu gesetzt. Ohne Wiedererkennung klappt
 * jedes <details>, das Rafael aufmacht, binnen einer halben Minute unter seinen
 * Haenden wieder zu — genau die Sorte Aerger, die er in der Nacht auf den
 * 10.09.2026 ein Dutzend Mal hatte.
 *
 * Der Text der <summary> taugt NICHT als Schluessel: er traegt Zaehler und
 * Uhrzeiten ("Heute · 2 Eintraege", "Coach, 02:55Z"), und mehrere heissen
 * schlicht "Details". Genommen wird deshalb, in dieser Reihenfolge:
 *   1. data-merker — was der Coach ausdruecklich benennt, ueberlebt auch einen Umbau,
 *   2. der Platz im Baum (Kind-Nummern bis zur Wurzel) — stabil, solange die
 *      Uebersicht gleich gebaut ist, und das ist sie zwischen zwei Runden fast immer.
 * Aendert sich der Aufbau wirklich (eine Karte ist beantwortet), findet sich der
 * Schluessel nicht wieder und der Zuklapper steht auf seiner Vorgabe. Das ist der
 * gewollte Ausgang: lieber einmal zu, als den falschen Abschnitt aufgeklappt.
 */
function zuklapperSchluessel(el: Element, wurzel: Element): string {
  const merker = (el as HTMLElement).dataset?.merker;
  if (merker) return `m:${merker}`;
  const teile: string[] = [];
  let k: Element | null = el;
  while (k && k !== wurzel) {
    const eltern: Element | null = k.parentElement;
    if (!eltern) break;
    teile.push(String(Array.prototype.indexOf.call(eltern.children, k)));
    k = eltern;
  }
  return `p:${teile.reverse().join('/')}`;
}

/** Welche Zuklapper stehen gerade offen? */
function offeneZuklapper(wurzel: HTMLElement): Set<string> {
  const offen = new Set<string>();
  for (const d of Array.from(wurzel.querySelectorAll('details'))) {
    if (d.open) offen.add(zuklapperSchluessel(d, wurzel));
  }
  return offen;
}

/**
 * Welche Schalter hat der Nutzer gesetzt?
 *
 * Rafael, 10.09.: "mach mir vielleicht ein umschalten ... sortierung nach
 * dringlichkeit ... oder platzsparender". Die Uebersicht baut solche Umschalter
 * ohne Javascript, allein mit `input:checked ~ ... { order: N }`. Der Zustand
 * lebt dann im DOM — und der Austausch unten wirft ihn weg, wie er es mit den
 * Zuklappern taete. Seine Wahl haette 30 Sekunden gehalten.
 *
 * Erkannt wird ueber die `id`, weil die Uebersicht sie vergibt (`cu-s-dring`)
 * und sie ueber alle Neuschreibungen dieselbe bleibt. Ein Schalter ohne `id`
 * ist nicht wiederfindbar und wird bewusst nicht gerettet.
 */
function gesetzteSchalter(wurzel: HTMLElement): Set<string> {
  const gesetzt = new Set<string>();
  for (const i of Array.from(wurzel.querySelectorAll<HTMLInputElement>('input[type="radio"][id], input[type="checkbox"][id]'))) {
    if (i.checked) gesetzt.add(i.id);
  }
  return gesetzt;
}

/*
 * DIE WAHL UEBERLEBT AUCH DAS VERLASSEN DER FLAECHE.
 *
 * Die Rettung oben haelt den Schalter ueber den 30-Sekunden-Austausch — aber nur,
 * solange die Komponente steht. Rafael geht auf eine Buehne und zurueck, die
 * Flaeche wird neu aufgebaut, und seine Wahl ist die Vorgabe aus dem HTML.
 * Sein Satz am 10.09. 09:3xZ: "Wenn ich auf 'Nur Partner' schalte und dann vorne
 * zurueckgehe, soll er natuerlich auf 'Nur Partner' bleiben. Jetzt muss ich wieder
 * auf 'Nur Partner' gehen."
 *
 * Deshalb liegt die Wahl zusaetzlich im sessionStorage — pro Flaeche ein Schluessel,
 * damit Rennbahn und Uebersicht sich nicht gegenseitig ueberschreiben. sessionStorage
 * und nicht localStorage: die Wahl gehoert zu dieser Sitzung am Bildschirm, nicht auf
 * ewig. Faellt der Speicher aus (privater Modus, gesperrt), bleibt es beim alten
 * Verhalten — ein Schalter, der nichts merkt, ist aergerlich; eine Flaeche, die
 * deswegen gar nicht laedt, waere schlimmer.
 */
// Seit 21.10.2026 in shared/guide-hausfilter.ts, weil auch der Server den Schluessel
// kennt (/api/capture/guide?haus=…). Name hier beibehalten, Verhalten unveraendert.
const schalterSchluessel = guideSchalterSchluessel;

function gemerkteSchalter(pfad: string): Set<string> {
  try {
    const roh = sessionStorage.getItem(schalterSchluessel(pfad));
    if (!roh) return new Set();
    const liste = JSON.parse(roh);
    return Array.isArray(liste) ? new Set(liste.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

const FILTER_EVENT = 'guide-hausfilter-geaendert';

/**
 * DER HAUSFILTER IST ZWEIWERTIG — Dev ODER Partner, nie beides gemischt.
 *
 * Rafael, 12.09.2026, ausdruecklich als Korrektur einer ersten (falschen) Fassung
 * mit einer dritten Option "Beide Haeuser": "Fuer mich gibt es nur Partner und
 * Dev. Sein Umschalter ist ZWEIWERTIG." Eine dritte, mischende Option war genau
 * das Problem, das er gemeldet hat ("heute mischen sich beide Haeuser in seiner
 * Flaeche") — sie blieb bisher sogar die VORGABE, solange niemand aktiv umschaltet.
 *
 * `null` heisst: noch nie umgeschaltet. Der Aufrufer (Hofansicht) loest das beim
 * ersten Laden gegen das eigene Haus des Servers auf (`hier_haus` aus der Antwort
 * von `/guide/buehnen`) und schreibt das Ergebnis zurueck — von da an ist die Wahl
 * explizit und ueberlebt die Sitzung am Bildschirm (sessionStorage, wie gehabt).
 */
export function useGuideHausfilter(): 'dev' | 'partner' | null {
  const lesen = (): 'dev' | 'partner' | null => {
    const ids = gemerkteSchalter(GUIDE_HAUSFILTER_PFAD);
    return ids.has(GUIDE_HAUS_SCHALTER.dev) ? 'dev' : ids.has(GUIDE_HAUS_SCHALTER.partner) ? 'partner' : null;
  };
  const [haus, setHaus] = useState(lesen);
  useEffect(() => {
    const aktualisieren = () => setHaus(lesen());
    window.addEventListener(FILTER_EVENT, aktualisieren);
    return () => window.removeEventListener(FILTER_EVENT, aktualisieren);
  }, []);
  return haus;
}

/**
 * DEN HAUSFILTER SETZEN — seit 11.09.2026 aus der Filterleiste des Hofes, seit
 * 12.09.2026 ZWEIWERTIG (siehe useGuideHausfilter).
 *
 * Bewusst DERSELBE Speicherplatz und dasselbe Ereignis wie bisher: jede gelieferte
 * Flaeche liest unveraendert weiter, und es gibt keine zweite Wahrheit darueber,
 * welches Haus gerade gezeigt wird.
 */
export function setzeGuideHausfilter(wert: 'dev' | 'partner'): void {
  const ids = gemerkteSchalter(GUIDE_HAUSFILTER_PFAD);
  ids.delete(GUIDE_HAUS_SCHALTER.dev);
  ids.delete(GUIDE_HAUS_SCHALTER.partner);
  ids.add(GUIDE_HAUS_SCHALTER[wert]);
  merkeSchalter(GUIDE_HAUSFILTER_PFAD, ids);
}

function merkeSchalter(pfad: string, ids: Set<string>): void {
  try {
    sessionStorage.setItem(schalterSchluessel(pfad), JSON.stringify(Array.from(ids)));
    if (pfad === GUIDE_HAUSFILTER_PFAD) window.dispatchEvent(new Event(FILTER_EVENT));
  } catch {
    /* Speicher nicht verfuegbar — die Wahl haelt dann nur bis zum Verlassen. */
  }
}

/**
 * Eine vom Coach geschriebene HTML-Flaeche (Rennbahn oder Uebersicht).
 *
 * Kennt der Server die Route noch nicht, antwortet er NICHT mit 404: die
 * Einzelseiten-Auslieferung schiebt fuer unbekannte Pfade die index.html mit
 * Status 200 heraus. Deshalb entscheidet die ANTWORTART, nicht der Status.
 */
export function CoachFlaeche({ pfad, hoehe, leerText, onAktion }: {
  pfad: 'bahn' | 'uebersicht';
  hoehe?: number;
  leerText: string;
  /** Klick auf ein Element mit data-guide-aktion. Ohne diesen Haken ist die Flaeche ein Bild. */
  onAktion?: (aktion: string, ziel: string, zusatz: Record<string, string>) => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * WANN der Coach diesen Text geschrieben hat.
   *
   * Rafael, 07.09.: "Guide zeigt Veraltetes." Der Server schickt `geschrieben_am`
   * seit jeher mit — die Flaeche hat es gelesen und weggeworfen. Ein Text, der vor
   * drei Stunden richtig war, sieht dann genauso aus wie einer von eben, und man
   * kann ihm nicht ansehen, welcher von beiden er ist.
   */
  const [geschriebenAm, setGeschriebenAm] = useState<string | null>(null);
  const [jetzt, setJetzt] = useState(() => Date.now());
  const behaelter = useRef<HTMLDivElement | null>(null);
  /** Die zuletzt EINGESETZTE HTML. Nicht `behaelter.innerHTML` — das liest der Browser normalisiert zurueck. */
  const eingesetzt = useRef<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const r = await fetch(`${API}/guide/${pfad}`);
      const art = r.headers.get('content-type') ?? '';
      /*
       * EINE NICHT-JSON-ANTWORT IST EIN FEHLER, KEIN LEERER COACH.
       *
       * Bis 10.09. stand hier `setHtml(null); setFehler(null)`: jede nginx-502-Seite
       * und jede index.html-Fallback-Antwort wurde stumm zu "eingerichtet: nein" —
       * und die Flaeche schrieb "Der Coach hat noch keine Uebersicht geschrieben".
       * Das ist eine Fehlaussage UEBER EINEN DRITTEN, und sie kostete Rafael am
       * 10.09. um 11:28Z zwei Bilder und eine Rueckfrage, waehrend die Datei die
       * ganze Zeit dalag (105.257 B, `eingerichtet: true`, in beiden Haeusern
       * gemessen). Ursache war ein 45-Sekunden-Baufenster, in das der Abruf fiel.
       *
       * Und: der zuletzt GUTE Text bleibt stehen. Ihn wegzuwerfen macht aus einer
       * Stoerung von 45 Sekunden eine leere Seite — mit Fehlerzeile daneben ist der
       * alte Text sichtbar gestoert (die Altersangabe steht ohnehin darunter), aber
       * er ist da.
       */
      if (!art.includes('application/json')) {
        setFehler(`Antwort war kein JSON (Status ${r.status}, Art ${art || 'ohne Angabe'}) — meist ein Baufenster. Der Text bleibt stehen, bis der naechste Abruf gelingt.`);
        return;
      }
      if (r.status === 404) { setFehler((await r.json()).error ?? 'Übersicht nicht gefunden (404)'); return; }
      if (!r.ok) { setFehler(`${r.status}`); return; }
      const d = await r.json();
      setHtml(d.eingerichtet ? d.html : null);
      setGeschriebenAm(typeof d.geschrieben_am === 'string' ? d.geschrieben_am : null);
      setFehler(null);
    } catch (e) {
      setFehler(String(e));
    }
  }, [pfad]);

  useEffect(() => { laden(); }, [laden]);
  useLiveChannel({ label: `Guide-${pfad}`, onMessage: (m: any) => { if (m?.type === 'guide-updated') laden(); }, onResync: laden });

  /*
   * Nachladen im Takt — der Kommentar unten nahm das seit jeher an ("die HTML wird
   * alle 30s ersetzt"), ein Intervall gab es aber nie. Die Flaeche hing allein am
   * `guide-updated`-Ereignis; schreibt der Coach seine Datei, ohne dass sich der
   * Guide-Stand aendert, blieb der alte Text stehen, bis Rafael das Panel wechselt.
   * Der zweite Takt haelt nur die Altersangabe lebendig, ohne zu laden.
   */
  useEffect(() => {
    const a = setInterval(laden, 30_000);
    const b = setInterval(() => setJetzt(Date.now()), 15_000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [laden]);

  /*
   * DIE HTML SELBST EINSETZEN, statt sie React ueber `dangerouslySetInnerHTML`
   * hinzulegen. Zwei Dinge muessen den Austausch ueberleben, und React kennt beide nicht:
   *
   *   OFFENE ZUKLAPPER — siehe `zuklapperSchluessel`. React ersetzt den Inhalt,
   *   das `open` des Nutzers ist weg.
   *
   *   GESETZTE SCHALTER — siehe `gesetzteSchalter`. Rafaels Wahl "nach Dringlichkeit"
 *   oder "knapp" ist ein `:checked` im DOM und sonst nirgends.
 *
 *   DIE SCROLLPOSITION — wer weit unten in der Themen-Landkarte liest, stand nach
   *   dem Austausch wieder oben. Der Behaelter selbst bleibt zwar stehen (nur sein
   *   Inhalt wird getauscht), aber sobald der neue Text kuerzer ist, kappt der
   *   Browser scrollTop; deshalb wird sie danach ausdruecklich zurueckgesetzt.
   *
   * Ist die HTML unveraendert, wird GAR NICHTS angefasst — der haeufigste Fall auf
   * einem ruhigen Vormittag, und der billigste.
   */
  useEffect(() => {
    const el = behaelter.current;
    if (!el || html === null) return;
    if (eingesetzt.current === html) return;

    const offen = offeneZuklapper(el);
    // Beim ERSTEN Einsetzen steht im DOM noch nichts Gewaehltes — dann zaehlt, was
    // Rafael zuletzt gewaehlt hat, bevor er die Flaeche verlassen hat.
    const imDom = gesetzteSchalter(el);
    const geschaltet = eingesetzt.current === null ? gemerkteSchalter(pfad) : imDom;
    const stand = el.scrollTop;

    el.innerHTML = html;
    eingesetzt.current = html;

    // Dichte ist unabhängig von der Sortierung. Die gelieferten CSS-Regeln
    // verwenden :checked und tragen beide Einstellungen bereits gleichzeitig.
    // Nur die alte Radiogruppe verhindert bisher „dringlich UND kompakt“.
    if (pfad === 'uebersicht') {
      const kompakt = el.querySelector<HTMLInputElement>('.cu-sortier > #cu-s-knapp');
      if (kompakt) {
        kompakt.type = 'checkbox';
        kompakt.name = 'cu-dichte';
        const label = el.querySelector<HTMLLabelElement>('label[for="cu-s-knapp"]');
        if (label) {
          label.textContent = 'Kompakt';
          label.title = 'Hintergrundtexte einklappen; die gewählte Sortierung bleibt erhalten. Erneut klicken für alle Details.';
        }
      }
    }

    if (offen.size) {
      for (const d of Array.from(el.querySelectorAll('details'))) {
        if (offen.has(zuklapperSchluessel(d, el))) d.open = true;
      }
    }
    /*
     * Der neue Text bringt seinen eigenen Vorgabe-Schalter mit (`checked` im
     * HTML). Ein gesetzter Schalter im selben Namen hebt ihn auf, deshalb genuegt
     * es, die gemerkten wieder zu setzen — die Vorgabe faellt von selbst weg.
     */
    for (const id of geschaltet) {
      const i = el.querySelector<HTMLInputElement>(`input[id="${CSS.escape(id)}"]`);
      if (i) i.checked = true;
    }
    // Was jetzt im DOM steht, ist die geltende Wahl — auch die Vorgabe des HTML,
    // falls Rafael noch nie geschaltet hat. Sie wird gemerkt, damit sie den naechsten
    // Weg ueber eine Buehne ueberlebt.
    merkeSchalter(pfad, gesetzteSchalter(el));
    // Erst NACH dem Aufklappen: die offenen Abschnitte bestimmen die Hoehe mit.
    if (stand > 0) el.scrollTop = stand;
  }, [html, pfad]);

  useEffect(() => {
    const el = behaelter.current;
    if (!el) return;
    // Die Inputs stammen aus innerHTML, nicht aus React. Dessen synthetisches
    // onChange verfolgt sie nicht zuverlässig; der native Wechsel ist die Quelle.
    const merken = () => merkeSchalter(pfad, gesetzteSchalter(el));
    el.addEventListener('change', merken);
    return () => el.removeEventListener('change', merken);
  }, [html, pfad]);

  if (!html && fehler) {
    return <div role="alert" style={{ padding: '6px 12px', fontSize: 12, color: 'var(--tn-red)' }}>{fehler}</div>;
  }
  if (!html) {
    return <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--tn-text-muted)' }}>{leerText}</div>;
  }
  /*
   * KLICK-FANG — die Flaeche ist eine Ansicht, kein Browser.
   *
   * Der Coach schreibt die HTML, wir machen ihre Links lebendig (Arbeitsteilung,
   * CLAUDE.md 10:49Z). Gefangen wird EIN Klick auf dem Behaelter statt Haken an
   * jedem Element: die HTML wird alle 30s ersetzt, einzeln angehaengte Haken waeren
   * nach dem ersten Austausch weg.
   *
   * KEIN LINK NAVIGIERT VON SELBST. Ein <a href> in einer eingebetteten Flaeche wuerde
   * das ganze CUI wegnavigieren — deshalb wird jeder Klick abgefangen, und nur
   * bekannte Aktionen fuehren zu etwas.
   *
   * Eine UNBEKANNTE Aktion wird gesagt, nicht verschluckt: der Coach baut etwas ein,
   * es passiert nichts, und ohne diese Meldung suchte er den Fehler in seiner HTML.
   */
  const klick = (e: React.MouseEvent<HTMLDivElement>) => {
    const treffer = (e.target as HTMLElement).closest<HTMLElement>('[data-guide-aktion]');
    if (!treffer) return;
    e.preventDefault();
    e.stopPropagation();
    const aktion = treffer.dataset.guideAktion ?? '';
    const ziel = treffer.dataset.guideZiel ?? '';
    if (!onAktion) return;
    const zusatz: Record<string, string> = {};
    for (const [k, v] of Object.entries(treffer.dataset)) {
      // data-guide-panels -> panels, data-guide-projekt -> projekt, …
      if (k.startsWith('guide') && k !== 'guideAktion' && k !== 'guideZiel' && typeof v === 'string') {
        zusatz[k.slice(5).toLowerCase()] = v;
      }
    }
    onAktion(aktion, ziel, zusatz);
  };

  /*
   * Das Alter in Worten. Ab einer halben Stunde faellt es auf — nicht als Fehler
   * (ein ruhiger Vormittag braucht keine neue Uebersicht), sondern damit Rafael
   * weiss, ob er einen Text von eben oder von heute frueh liest.
   *
   * Fehlt der Zeitpunkt, wird er NICHT geschaetzt: dann steht da, dass er fehlt.
   */
  const alter = (() => {
    if (!geschriebenAm) return { text: 'ohne Zeitpunkt geschrieben', alt: true };
    const ms = jetzt - new Date(geschriebenAm).getTime();
    if (!Number.isFinite(ms)) return { text: `Zeitpunkt unlesbar (${geschriebenAm})`, alt: true };
    const min = Math.max(0, Math.round(ms / 60_000));
    if (min < 1) return { text: 'gerade eben geschrieben', alt: false };
    if (min < 60) return { text: `vor ${min} min geschrieben`, alt: min >= 30 };
    const std = Math.floor(min / 60);
    return { text: `vor ${std} h ${min % 60} min geschrieben`, alt: true };
  })();

  return (
    <div style={{ height: hoehe, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {fehler && <div role="alert" style={{ padding: '6px 12px', fontSize: 12, color: 'var(--tn-red)' }}>
        {fehler} — letzter geladener Stand bleibt sichtbar.
      </div>}
      {/* Der Inhalt kommt aus dem Effekt oben, nicht von React — sonst faellt jeder
          aufgeklappte Abschnitt und die Leseposition alle 30 s zurueck. */}
      <div
        ref={behaelter}
        data-ai-id={`guide-flaeche-${pfad}`}
        onClick={klick}
        style={{ flex: 1, overflow: 'auto', padding: '4px 12px', minHeight: 0 }}
      />
      <div
        data-ai-id={`guide-flaeche-alter-${pfad}`}
        style={{
          flex: '0 0 auto', padding: '2px 12px 4px', fontSize: 10, lineHeight: 1.3,
          color: alter.alt ? 'var(--tn-orange)' : 'var(--tn-text-muted)',
        }}
      >
        {pfad === 'uebersicht' ? 'Übersicht' : 'Rennbahn'} vom Coach — {alter.text}
      </div>
    </div>
  );
}


/**
 * DIE COACH-SPALTE.
 *
 * Rafael: "Rechts laeuft immer der Chat, der die Seite gebaut hat, damit ich im
 * richtigen Kontext weiterarbeite." Solange keine Karte offen ist, steht hier der
 * Coach — er ist der, der die Vorlage steuert.
 *
 * Der frueher zweite Chat ("Lage") ist weg: Rafael hat ihn ausdruecklich
 * abgeschafft. An seine Stelle ist die Entscheidungsgrundlage getreten.
 */
/**
 * NEUSTART-KNOPF FUER KANZLER UND VIZEKANZLER (Rafael, 21.09.2026: "ich brauch
 * auch einen neustart button fuer kanzler und vize im GUI").
 *
 * Den Coach ANZEIGEN konnte man schon; ERSETZEN nicht. `/coach/starten` weigert
 * sich bewusst, einen zweiten zu oeffnen, und zeigt bei erneutem Druck den
 * eingetragenen — das hilft genau dann nicht, wenn der eingetragene nicht mehr
 * weiterkommt (gemessen 21.09.2026: 163 MB Sitzung, 155 Kompaktierungen im
 * Koordinations-Ordner, eine Uebergabe an sich selbst mit acht Nachrichten).
 *
 * RUECKFRAGE VOR DEM DRUCK, weil die laufende Sitzung dabei beendet wird — was
 * nur in ihrem Verlauf steht, ist danach weg. Der Knopf nennt deshalb die
 * Kennung, die er beendet.
 */
function CoachNeustart({ sessionId, onFertig }: { sessionId: string; onFertig?: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const druecken = async () => {
    if (!confirm(`Sitzung ${sessionId.slice(0, 8)} beenden und einen frischen Coach oeffnen?\n\n`
      + 'Der bisherige Verlauf bleibt als Datei erhalten, die Sitzung selbst ist danach beendet.')) return;
    setLaeuft(true); setFehler(null);
    try {
      const r = await fetch('/api/guide/coach/neu-starten', { method: 'POST', signal: AbortSignal.timeout(60000) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setFehler(d?.error || `HTTP ${r.status}`); return; }
      onFertig?.();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally { setLaeuft(false); }
  };
  return (
    <>
      <button
        data-ai-id="guide-coach-neu-starten"
        type="button"
        onClick={() => void druecken()}
        disabled={laeuft}
        title={`Beendet ${sessionId.slice(0, 8)} und oeffnet einen frischen Coach`}
        style={{ padding: '2px 8px', fontSize: 11, borderRadius: 4, cursor: laeuft ? 'wait' : 'pointer',
          background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)', fontWeight: 400 }}
      >{laeuft ? 'startet neu …' : '⟳ Sitzung neu starten'}</button>
      {fehler && <span style={{ fontSize: 11, color: 'var(--tn-fehler)', marginLeft: 8, fontWeight: 400 }}>{fehler}</span>}
    </>
  );
}

/*
 * EINE KOPFZEILE (Rafael, 03.10.2026: „die leisten ganz oben sind chaotisch").
 * Bis dahin setzte die Spalte ihren eigenen Kopf („Dev — Coach" + ⟳ neu
 * starten) UEBER den Kopf des Chats, und der Chat zeigte darunter noch einmal
 * „Coach" — drei Titelzeilen fuer ein Fenster. Jetzt traegt der Chat den Titel
 * selbst, und der Neustart (selten, folgenreich, fragt nach) liegt in seinem
 * ⋯-Menue.
 */
export function CoachSpalte({ chat, titel, onNeuGestartet, ohneNeustart, kopfZusatz }: { chat: { sessionId: string; accountId?: string } | null; titel: string; onNeuGestartet?: () => void; /** Der Neustart gilt dem Hauskanzler — bei einem Partner nicht rufen. */ ohneNeustart?: boolean; /** Kleines Bedienelement in der Kopfzeile (eigenstaendiges Coach-Fenster: „Ansicht"). */ kopfZusatz?: React.ReactNode }) {
  if (!chat) {
    return (
      <div style={{ padding: 12, fontSize: 13, color: 'var(--tn-text-muted)' }}>
        Noch kein Chat hinterlegt — die zustaendige Sitzung traegt sich selbst ein.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <CuiLitePanel
          panelId="guide-chat-koordinator"
          accountId={chat.accountId}
          initialSessionId={chat.sessionId}
          isTabVisible
          titel={titel}
          kopfZusatz={kopfZusatz}
          menueZusatz={ohneNeustart ? undefined : <CoachNeustart sessionId={chat.sessionId} onFertig={onNeuGestartet} />}
        />
      </div>
    </div>
  );
}

/**
 * DIE COACH-SPALTE DES ANDEREN HAUSES.
 *
 * Rafael, 09.09.2026: "bring endlich den zweiten Coach zum Laufen ... sowohl
 * den Dev-Coach als auch den Partner-Coach sehen" — und, nach der ersten
 * (nachgebauten, nur lesenden) Fassung: "Das sollte eigentlich nur eine
 * Voreinstellung fuer eine Weiterleitung des Panels vom Partner-Server sein,
 * oder? Kann nicht so schwierig sein." Er hat recht: `BrowserPanel` rendert
 * bereits ein <webview> mit eigener Session — die einzige offene Frage war,
 * ob dessen (geteilte) Partition drueben schon angemeldet ist. GEMESSEN
 * 09.09. 10:29Z: ein Browser-Panel mit derselben geteilten Partition, auf
 * `https://partner.werking.tools/?dgr=1` navigiert, verbindet sofort per WS
 * und zeigt den echten, bedienbaren Coach-Chat — keine Anmeldung noetig,
 * nichts geraten.
 *
 * KORREKTUR 09.09. 10:58Z: `?dgr=1` haengt den GANZEN Fern-Guide ein (Themen,
 * Buehne, Tool Hub, ein zweites Browser-Panel) — Rafael dreimal: "im guide
 * wird der ganze partner verknuepft statt nur dem kleinen partner coach" /
 * "der coach rechts soll so aussehen wie der coach links" / "einfach das
 * panel weiterleiten". Es fehlte ein Deep-Link, der NUR den Coach-Chat
 * rendert — jetzt `StandalonePanel.tsx`: `?panel=coach-chat`, dieselbe
 * Komponente (CoachSpalte) wie links, admin-only geprueft.
 */
/** Feste panelId der Fremd-Coach-Kachel: Console, Screenshot, navigate/reload ueber /api/browser-*. Es gibt genau eine Instanz (App.tsx, Guide-Flaeche). */
export const FREMD_COACH_PANEL_ID = 'guide-fremd-coach';

export function FremdCoachSpalte() {
  const [haus, setHaus] = useState<string | null>(null);
  const [chat, setChat] = useState<{ sessionId: string; accountId?: string; label?: string } | null>(null);
  const [webviewUrl, setWebviewUrl] = useState<string | null>(null);
  // Server meldet laut, wenn das Ticket fuers andere Haus fehlt (guide-coach.ts webviewZielFuer).
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuVersuch, setNeuVersuch] = useState(0);
  // Zaehlt erfolgreiche Ladungen: nach ⟳ baut die Kachel mit der NEU geholten Adresse neu auf (auch wenn sie gleich lautet).
  const [ladeNr, setLadeNr] = useState(0);
  /*
   * Rafael 20.10.2026 07:16Z „der vize ist nicht da“ (Befund ae690328): eine einzelne Antwort OHNE Ticket
   * (Ersatzadresse + Hinweis) ueberschrieb die bereits geladene Ticket-Adresse, baute die Webview auf die
   * rohe Adresse neu auf (Login bzw. „Internal Server Error“) und galt als Erfolg — danach kein Abruf mehr
   * bis ⟳ oder Reload. Jetzt: die letzte Ticket-Adresse bleibt stehen, der Hinweis traegt die Uhrzeit, und
   * der Abruf wiederholt sich mit wachsendem Abstand (15 s … 5 min), bis wieder ein Ticket kommt.
   * Refs statt State, damit ⟳ (neuer Effekt-Lauf) den letzten Stand nicht vergisst.
   */
  const ticketAdresse = useRef<string | null>(null);
  const unbestaetigtSeit = useRef<number | null>(null);
  /*
   * 26.10.2026 (ERR_NAME_NOT_RESOLVED nach Netzwechsel am Mac, Befund cui-partner-fenster-20261026):
   * Die Kachel laedt ein Einmal-Ticket (5 min). Ein reload() derselben Adresse hilft nach Ablauf nicht.
   * Darum holen ↻ und ein Ladefehler ein NEUES Ticket. Automatisch genau einmal je Fehlerserie (bis zum
   * naechsten erfolgreichen Laden), mit wachsendem Abstand ueber die letzten 30 min (5 s … 5 min);
   * danach bleibt die Fehlermeldung der Kachel mit Knopf stehen.
   */
  const autoTicketInSerie = useRef(false);
  const autoTicketZeiten = useRef<number[]>([]);
  const autoTicketTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigationGeneration = useRef(0);
  const beiNavigationStart = useCallback(() => {
    navigationGeneration.current += 1;
    if (autoTicketTimer.current !== null) clearTimeout(autoTicketTimer.current);
    autoTicketTimer.current = null;
  }, []);
  const neuesTicketHolen = useCallback(() => {
    if (autoTicketTimer.current !== null) { clearTimeout(autoTicketTimer.current); autoTicketTimer.current = null; }
    setNeuVersuch(v => v + 1);
  }, []);
  const beiLadeFehler = useCallback((): boolean => {
    if (autoTicketInSerie.current) return false; // schon einmal automatisch neu geholt: Fehler bleibt sichtbar
    autoTicketInSerie.current = true;
    const jetzt = Date.now();
    autoTicketZeiten.current = autoTicketZeiten.current.filter(t => jetzt - t < 30 * 60_000);
    const abstand = Math.min(300_000, 5_000 * 2 ** autoTicketZeiten.current.length);
    autoTicketZeiten.current.push(jetzt);
    if (autoTicketTimer.current !== null) clearTimeout(autoTicketTimer.current);
    autoTicketTimer.current = setTimeout(() => { autoTicketTimer.current = null; setNeuVersuch(v => v + 1); }, abstand);
    return true;
  }, []);
  const beiGeladen = useCallback(() => { autoTicketInSerie.current = false; }, []);
  useEffect(() => () => { if (autoTicketTimer.current !== null) clearTimeout(autoTicketTimer.current); }, []);

  useEffect(() => {
    let abgebrochen = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    let versuch = 0;
    let unbestaetigtVersuch = 0;
    const generation = navigationGeneration.current;
    const laden = async () => {
      if (abgebrochen || generation !== navigationGeneration.current) return;
      controller = new AbortController();
      const frist = setTimeout(() => controller?.abort(), 15000);
      let naechsterAbstand = () => Math.min(5000, 1000 * ++versuch);
      try {
        const r = await fetch(`${API}/guide/coach/anderes-haus?ansicht=fenster`, { signal: controller.signal });
        const d = await r.json();
        if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
        if (abgebrochen || generation !== navigationGeneration.current) return;
        setHaus(d.haus ?? null);
        setChat(d.chat ?? null);
        setFehler(null);
        const serverHinweis = typeof d.webviewHinweis === 'string' && d.webviewHinweis ? d.webviewHinweis : null;
        if (d.chat && d.webviewUrl && !serverHinweis) {
          // Ticket da: letzter bestaetigter Stand, Hinweis weg, Kachel neu aufbauen.
          ticketAdresse.current = d.webviewUrl; unbestaetigtSeit.current = null; unbestaetigtVersuch = 0;
          setHinweis(null); setWebviewUrl(d.webviewUrl); setLadeNr(n => n + 1);
          return;
        }
        if (d.chat && serverHinweis) {
          unbestaetigtSeit.current ??= Date.now();
          const seit = `${new Date(unbestaetigtSeit.current).toISOString().slice(11, 16)}Z`;
          const hausName = d.haus === 'partner' ? 'Partner-Haus' : 'anderen Haus';
          if (ticketAdresse.current) {
            // Letzter Stand bleibt stehen; die Webview wird NICHT auf die Ersatzadresse neu aufgebaut.
            setHinweis(`Anmeldung im ${hausName} seit ${seit} nicht bestätigt — letzter Stand bleibt. ${serverHinweis}`);
          } else {
            setHinweis(`${serverHinweis} Seit ${seit} nicht bestätigt.`);
            setWebviewUrl(d.webviewUrl ?? null);
          }
          naechsterAbstand = () => Math.min(300_000, 15_000 * 2 ** unbestaetigtVersuch++);
        } else {
          setHinweis(null);
          setWebviewUrl(d.webviewUrl ?? null);
        }
      } catch (e) {
        if (abgebrochen || generation !== navigationGeneration.current) return;
        setFehler(e instanceof Error && e.name !== 'AbortError' ? e.message : 'Verbindung dauert länger. Neuer Versuch läuft …');
      } finally { clearTimeout(frist); }
      if (!abgebrochen && generation === navigationGeneration.current) timer = setTimeout(laden, naechsterAbstand());
    };
    void laden();
    return () => { abgebrochen = true; clearTimeout(timer); controller?.abort(); };
  }, [neuVersuch]);

  const titel = haus === 'partner' ? 'Partner' : haus === 'dev' ? 'Dev' : '…';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {/* Die Adresszeile der Kachel ist zu (sie zeigt immer dieselbe Fern-Adresse);
          neu laden, im neuen Tab oeffnen und die Zeile selbst stehen hier im Kopf. */}
      <div style={{ padding: '4px 10px', borderBottom: '1px solid var(--tn-border)', fontSize: 13, fontWeight: 600, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ flex: 1, minWidth: 0 }}>{titel}</span>
        {chat && webviewUrl && (
          // ↻ holt ein neues Einmal-Ticket (neue Adresse, Kachel baut neu auf) — ein reload() der alten Ticket-Adresse scheitert nach 5 min.
          <button type="button" data-ai-id="guide-fremd-coach-neu-laden" title="Neue Anmeldung holen und Kachel neu laden" aria-label="Neue Anmeldung holen und Kachel neu laden"
            onClick={neuesTicketHolen}
            style={{ border: 'none', background: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', padding: '0 4px', fontSize: 13, fontWeight: 400 }}>↻</button>
        )}
        {chat && webviewUrl && ([
          [OEFFNEN_EREIGNIS, '↗', 'In neuem Tab öffnen', 'guide-fremd-coach-neuer-tab'],
          [ADRESSZEILE_EREIGNIS, '🔗', 'Adresszeile ein- oder ausblenden', 'guide-fremd-coach-adresszeile'],
        ] as const).map(([ereignis, zeichen, text, aiId]) => (
          <button key={aiId} type="button" data-ai-id={aiId} title={text} aria-label={text}
            onClick={() => window.dispatchEvent(new CustomEvent(ereignis, { detail: { panelId: FREMD_COACH_PANEL_ID } }))}
            style={{ border: 'none', background: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', padding: '0 4px', fontSize: 13, fontWeight: 400 }}>{zeichen}</button>
        ))}
      </div>
      {hinweis && (
        <div role="alert" data-ai-id="guide-fremd-coach-hinweis" style={{ padding: '6px 10px', fontSize: 12, color: 'var(--tn-error,var(--tn-red))', borderBottom: '1px solid var(--tn-border)', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{hinweis}</span>
          <button data-ai-id="guide-fremd-coach-neu" title="Adresse neu holen und Kachel neu laden" onClick={neuesTicketHolen}>⟳</button>
        </div>
      )}
      {!chat || !webviewUrl ? (
        <div style={{ padding: 12, fontSize: 13, color: 'var(--tn-text-muted)' }}>
          {fehler ? `Nicht erreichbar: ${fehler}` : `Noch kein Coach im Haus ${titel} eingetragen.`}
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          {/* key: eine neu geholte Adresse (⟳) baut die Kachel neu auf, statt die alte weiterzuzeigen. */}
          <BrowserPanel key={`${webviewUrl}#${ladeNr}`} initialUrl={webviewUrl} panelId={FREMD_COACH_PANEL_ID} neuLadenBeimEinblenden adresszeileEinklappbar
            cuiChatGatewayRecovery onLadeFehler={beiLadeFehler} onNeuLaden={neuesTicketHolen} onGeladen={beiGeladen} onNavigationStart={beiNavigationStart} />
        </div>
      )}
    </div>
  );
}

/**
 * DIE RENNBAHN — die Strecke des Tages, oben in der Guide-Spalte.
 *
 * Daten vom Coach (koordination/rennbahn.py -> rennbahn.json), ausgeliefert von
 * GET /api/guide/rennbahn. Bewusst DATEN statt fertigem HTML: die Abschnitte sind
 * Sprungziele (kartenId), und Klicklogik in zwei Repos zu pflegen geht schief.
 *
 * PAUSEN: der Coach erfindet keine Zeiten. Steht nichts im Kalender, liefert er
 * `pausen_quelle: "keine"` und den Satz, der dann dastehen soll. Eine leere
 * Pausenliste ist deshalb ein SICHTBARER ZUSTAND und sieht nicht aus wie ein
 * Fehler — es ist der Unterschied zwischen "heute keine Pause geplant" und
 * "die Pausen sind kaputt".
 */
interface Abschnitt {
  art: string;
  zeit: string | null;
  titel: string;
  zustand?: string;
  kartenId?: string;
  /*
   * Alles ab hier rechnet der SERVER (GET /guide/rennbahn), nicht der Coach-Generator.
   * Bis 06.09. kam `fehlt` aus rennbahn.json und war damit so alt wie die Datei:
   * Rafael sah um 13:3x "fehlt: Risiken +2 / in Arbeit" bei Karten, die seit 13:2x
   * bereit waren. Eine zweite Meinung ueber denselben Zustand ist keine Redundanz,
   * sondern ein Widerspruch mit Zeitverzoegerung.
   */
  bereit?: boolean;
  /** Karte ist nicht mehr offen (beantwortet/abgegeben/…) — erledigt, nicht unfertig. */
  erledigt?: boolean;
  fehlt?: string[];
  karte_fehlt?: boolean;
  passt_zum_kopf?: boolean;
  kopfanstrengung?: string;
  gebaut_von?: string | null;
  seit?: string;
}

/** "seit 13:42Z" ist fuer Rafael keine Auskunft — "seit 18 Minuten" schon. */
function seitWann(iso?: string): string | null {
  if (!iso) return null;
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.round(ms / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `seit ${min} min`;
  const std = Math.floor(min / 60);
  return std < 24 ? `seit ${std} h` : `seit ${Math.floor(std / 24)} Tagen`;
}

const ART_ZEICHEN: Record<string, string> = {
  entscheidung: '◆',
  erledigt: '✓',
  pause: '☕',
  termin: '📅',
  naechstes: '→',
};

export function Rennbahn({ onKarte }: { onKarte?: (kartenId: string) => void }) {
  const [daten, setDaten] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const r = await fetch(`${API}/guide/rennbahn`);
      const d = await r.json();
      if (!r.ok) { setFehler(d?.error ?? `${r.status}`); setDaten(null); return; }
      setFehler(null);
      setDaten(d);
    } catch (e) {
      setFehler(String(e));
    }
  }, []);

  useEffect(() => {
    laden();
    // Der Coach erzeugt die Datei bei jedem Lauf neu; ein ruhiger Takt genuegt.
    const t = setInterval(laden, 60000);
    return () => clearInterval(t);
  }, [laden]);

  if (fehler) {
    return <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--tn-red)' }}>Strecke: {fehler}</div>;
  }
  if (!daten) return null;
  if (!daten.eingerichtet) {
    return <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--tn-text-muted)' }}>{daten.hinweis}</div>;
  }

  const abschnitte: Abschnitt[] = daten.abschnitte ?? [];
  /*
   * `zustand` ist die Angabe des Coach-Generators, `erledigt` die des Servers. Wo sie
   * sich widersprechen, gilt der Server — er hat in die Karte gesehen. Am 06.09.
   * standen so zwei laengst beantwortete Karten als offene Zeilen auf der Bahn, weil
   * die Datei sie noch als anstehend fuehrte.
   */
  const offenAlle = abschnitte.filter(a =>
    a.zustand !== 'erledigt' && a.zustand !== 'getroffen' && a.erledigt !== true);
  /*
   * DER SCHWERE-SCHALTER WIRKT AUCH HIER (Rafael, 06.09.: "das Umschalten
   * funktioniert noch nicht"). Er hat den Modus umgestellt und auf der Bahn aenderte
   * sich nichts — der Filter sass nur an der Vorlage, nicht an den Chips.
   * Ausgeblendetes wird UNTEN gezaehlt, nie stillschweigend weggelassen.
   */
  const offen = offenAlle.filter(a => a.passt_zum_kopf !== false);
  const zb = daten.zaehler_bahn ?? {};
  /*
   * NUR BEREITE KARTEN SIND EIN KLICK (Rafael, 06.09.): eine Karte anzubieten, hinter
   * der noch nichts steht, kostet ihn den Weg hin und zurueck. Was gebaut wird, bleibt
   * sichtbar — aber als ZAHL mit Wer und Seit-wann, nicht als Einladung.
   */
  const imBau = offen.filter(a =>
    a.art === 'entscheidung' && a.kartenId && a.bereit === false && !a.karte_fehlt && !a.erledigt);
  const zeigbar = offen.filter(a => !(a.art === 'entscheidung' && a.kartenId && a.bereit === false));

  return (
    <div data-ai-id="guide-rennbahn" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Dein Tag — {daten.wochentag}, {daten.datum}</span>
        <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
          {zb.bereit ?? 0} bereit · {zb.werden_gebaut ?? 0} werden gebaut
          {zb.erledigt ? ` · ${zb.erledigt} erledigt` : ''}
          {zb.ausgeblendet_durch_kopf ? ` · ${zb.ausgeblendet_durch_kopf} zu schwer fuer jetzt` : ''}
        </span>
      </div>

      {/* Erst das Offene: was noch vor ihm liegt. Erledigtes steht darunter als Beleg,
          nicht als Aufgabe. */}
      {zeigbar.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--tn-text-muted)' }}>
          {offenAlle.length > offen.length
            ? 'Fuer den eingestellten Kopf steht gerade nichts an — schalte oben um, um die schwereren zu sehen.'
            : 'Auf der Strecke steht gerade nichts Offenes.'}
        </div>
      )}
      {zeigbar.map((a, i) => (
        <div
          key={`${a.titel}-${i}`}
          onClick={() => { if (a.kartenId && onKarte) onKarte(a.kartenId); }}
          style={{
            display: 'flex', gap: 8, padding: '3px 0', fontSize: 13,
            cursor: a.kartenId && onKarte ? 'pointer' : 'default',
            alignItems: 'baseline',
          }}
        >
          <span style={{ color: 'var(--tn-text-muted)', minWidth: 42 }}>{a.zeit ?? '—'}</span>
          <span>{ART_ZEICHEN[a.art] ?? '·'}</span>
          <span style={{ flex: 1 }}>
            {a.titel}
            {a.karte_fehlt ? (
              <span style={{ color: 'var(--tn-red)', fontSize: 12 }}> — {a.fehlt?.[0] ?? 'Karte fehlt'}</span>
            ) : null}
          </span>
        </div>
      ))}

      {/*
        WAS GEBAUT WIRD — eine Zahl, keine Liste zum Anklicken.

        Rafael, 06.09.: "Ein Zustand 'in Arbeit', hinter dem niemand arbeitet, darf
        nicht existieren." Erzwingen kann das nur der Coach (wecken/spawnen). Hier
        steht deshalb, WER daran baut und SEIT WANN — damit ein Eintrag, hinter dem
        in Wahrheit niemand sitzt, sofort auffaellt statt sich als "in Arbeit" zu
        tarnen. Anonymes "in Arbeit" war genau die Tarnkappe.
      */}
      {imBau.length > 0 && (
        <div data-ai-id="guide-rennbahn-im-bau" style={{ marginTop: 6, fontSize: 12, color: 'var(--tn-text-muted)' }}>
          {imBau.length} {imBau.length === 1 ? 'Entscheidung wird' : 'Entscheidungen werden'} gerade gebaut:
          {' '}
          {imBau.map((a, i) => (
            <span key={`bau-${i}`}>
              {i > 0 ? ' · ' : ''}
              {a.titel}
              {a.gebaut_von ? ` (${a.gebaut_von}` : ' ('}
              {seitWann(a.seit) ? `${a.gebaut_von ? ', ' : ''}${seitWann(a.seit)}` : ''}
              {')'}
            </span>
          ))}
        </div>
      )}

      {/*
        PAUSEN: kein Eintrag ist eine Aussage, kein leeres Feld.

        Rafael, 06.09.: "Da kann er auch einen Platzhalter nehmen und stellt das halt
        noch ein." Deshalb stehen hier Stationen — aber SICHTBAR als Platzhalter, mit
        dem Satz des Coach dazu. Der Unterschied zwischen "so ist der Tag eingeteilt"
        und "so koennte er eingeteilt sein" muss lesbar bleiben; eine erfundene
        Uhrzeit, die aussieht wie eine echte, ist genau die Sorte Zahl, nach der
        Rafael sich richtet und dann danebenliegt.
      */}
      {daten.pausen_quelle === 'keine' && (
        <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed var(--tn-border)' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 13, color: 'var(--tn-text-muted)' }}>
            {['Vormittag', 'Mittagspause', 'Nachmittag', 'Feierabend'].map(name => (
              <span key={name} style={{ opacity: 0.55 }}>☕ {name} (Platzhalter)</span>
            ))}
          </div>
          {daten.pausen_hinweis && (
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--tn-text-muted)' }}>{daten.pausen_hinweis}</div>
          )}
        </div>
      )}
      {daten.kalender_fehler && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--tn-orange)' }}>Kalender: {daten.kalender_fehler}</div>
      )}
      {daten.unbekannte_arten?.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--tn-orange)' }}>
          Neue Abschnittsart(en) vom Coach, hier noch ohne eigene Darstellung: {daten.unbekannte_arten.join(', ')}
        </div>
      )}
    </div>
  );
}

/**
 * DREI KNOEPFE: schneller · gleich · langsamer.
 *
 * Der Wechseltest des Coach (06.09.) legt abwechselnd reine Textform und Hybridform
 * vor. Gemessen wird, was Rafael SAGT — nicht, was jemand aus der Klickzeit
 * herausliest. Der Wert landet an der Karte (GET /guide/karten liest der Coach mit);
 * bewusst kein zweiter Schreiber auf koordination/formtest.json.
 */
export function Formrueckmeldung({ kartenId, gesetzt }: { kartenId: string; gesetzt?: string }) {
  const [wert, setWert] = useState<string | null>(gesetzt ?? null);
  const [fehler, setFehler] = useState<string | null>(null);

  const melden = async (w: 'schneller' | 'gleich' | 'langsamer') => {
    try {
      const r = await fetch(`${API}/guide/karten/${encodeURIComponent(kartenId)}/form-rueckmeldung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wert: w }),
      });
      if (!r.ok) { setFehler((await r.json().catch(() => ({}))).error ?? `${r.status}`); return; }
      setWert(w);
      setFehler(null);
    } catch (e) {
      setFehler(String(e));
    }
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>Diese Form gelesen:</span>
      {(['schneller', 'gleich', 'langsamer'] as const).map(w => (
        <button
          key={w}
          data-ai-id={`guide-form-${w}`}
          onClick={() => melden(w)}
          style={{
            padding: '2px 8px', fontSize: 12, borderRadius: 10, cursor: 'pointer',
            background: wert === w ? 'rgba(122,162,247,0.15)' : 'transparent',
            color: wert === w ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
            border: `1px solid ${wert === w ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
          }}
        >
          {w}
        </button>
      ))}
      {fehler && <span style={{ fontSize: 11, color: 'var(--tn-red)' }}>{fehler}</span>}
    </div>
  );
}

/**
 * DER GUIDE-TEXT: Fliesstext oder Landkarte.
 *
 * Rafael, 06.09. (12:41Z), ueber die weisse Uebersichtsseite mit ihren drei Kaesten
 * ("Wie du heute dran bist", "Das eine, was jetzt vor dir liegt", "Deine Ziele"):
 * "Deutlich besser, aber noch eine Stufe zu viel." Statt Kaesten also Saetze — je
 * Thema einer, der sagt, wann es sich lohnt und warum er dafuer gebraucht wird, und
 * der in einem Klick endet.
 *
 * Die Saetze schreibt der COACH (koordination/guide-text.json), diese Flaeche
 * rendert sie. Chips stehen im Text als [[kartenId|Beschriftung]].
 *
 * Zwei Ansichten auf DENSELBEN Daten, kein zweiter Bestand: Fliesstext liest sich
 * am Stueck, die Landkarte zeigt je Thema Fortschritt und Chips nebeneinander.
 */
interface TextChip {
  kartenId: string;
  beschriftung: string;
  zustand?: string;
  fehlt?: string[];
}

interface TextThema {
  id: string;
  name: string;
  workspace?: string;
  absatz: string;
  chips?: TextChip[];
  /** Wie viele Karten des Themas fertig vorbereitet sind — und wie viele noch entstehen. */
  fortschritt?: { bereit: number; in_arbeit: number } | null;
  warum?: string;
}

/**
 * Eine Luecke kurz halten.
 *
 * Der Coach uebersetzt Pruefbefunde in lesbare Kurzform, aber er ist nicht die
 * einzige Quelle und irrt sich auch mal — im ersten Lauf am 06.09. standen ganze
 * Diagnoseabsaetze in `fehlt`. Die Flaeche schneidet deshalb selbst zu: gegen die
 * Textwand, gegen die diese Ansicht gebaut ist, hilft nur, dass BEIDE Seiten sie
 * verhindern. Der volle Text bleibt im Titel-Tooltip erhalten, nichts geht verloren.
 */
function kurz(text: string, max = 60): string {
  const eine = text.split(/(?<=[.:])\s/)[0].trim();
  return eine.length > max ? `${eine.slice(0, max - 1).trimEnd()}…` : eine;
}

/** Zerlegt einen Absatz in Text und [[kartenId|Beschriftung]]-Chips. */
function zerlege(absatz: string): Array<{ text: string } | { kartenId: string; label: string }> {
  const teile: Array<{ text: string } | { kartenId: string; label: string }> = [];
  const re = /\[\[([^\]|]+)\|([^\]]+)\]\]/g;
  let letzte = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(absatz))) {
    if (m.index > letzte) teile.push({ text: absatz.slice(letzte, m.index) });
    teile.push({ kartenId: m[1].trim(), label: m[2].trim() });
    letzte = m.index + m[0].length;
  }
  if (letzte < absatz.length) teile.push({ text: absatz.slice(letzte) });
  return teile;
}

function Absatz({ absatz, onKarte }: { absatz: string; onKarte: (id: string) => void }) {
  return (
    <>
      {zerlege(absatz).map((t, i) => 'text' in t ? (
        <span key={i}>{t.text}</span>
      ) : (
        <button
          key={i}
          data-ai-id={`guide-chip-${t.kartenId}`}
          onClick={() => onKarte(t.kartenId)}
          style={{
            display: 'inline-block', margin: '0 2px', padding: '1px 9px', fontSize: 13,
            borderRadius: 11, cursor: 'pointer', background: 'rgba(122,162,247,0.12)',
            color: 'var(--tn-blue)', border: '1px solid var(--tn-blue)',
          }}
        >
          {t.label}
        </button>
      ))}
    </>
  );
}

export function GuideText({ onKarte }: { onKarte: (kartenId: string) => void }) {
  const [daten, setDaten] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ansicht, setAnsicht] = useState<'text' | 'karte'>(
    () => (localStorage.getItem('guide-ansicht') === 'karte' ? 'karte' : 'text'),
  );

  const laden = useCallback(async () => {
    try {
      const r = await fetch(`${API}/guide/text`);
      const d = await r.json();
      if (!r.ok) { setFehler(d?.error ?? `${r.status}`); return; }
      setFehler(null); setDaten(d);
    } catch (e) { setFehler(String(e)); }
  }, []);
  useEffect(() => { laden(); const t = setInterval(laden, 60000); return () => clearInterval(t); }, [laden]);
  useLiveChannel({ label: 'Guide-Text', onMessage: (m: any) => { if (m?.type === 'guide-updated') laden(); }, onResync: laden });

  const umschalten = (a: 'text' | 'karte') => { setAnsicht(a); localStorage.setItem('guide-ansicht', a); };

  if (fehler) return <div style={{ fontSize: 12, color: 'var(--tn-red)' }}>Guide-Text: {fehler}</div>;
  if (!daten) return null;
  if (!daten.eingerichtet) {
    return <div style={{ fontSize: 13, color: 'var(--tn-text-muted)' }}>{daten.hinweis}</div>;
  }
  const themen: TextThema[] = daten.themen ?? [];

  return (
    <div data-ai-id="guide-text">
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {(['text', 'karte'] as const).map(a => (
          <button
            key={a}
            data-ai-id={`guide-ansicht-${a}`}
            onClick={() => umschalten(a)}
            style={{
              padding: '2px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
              background: ansicht === a ? 'rgba(122,162,247,0.15)' : 'transparent',
              color: ansicht === a ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
              border: `1px solid ${ansicht === a ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
            }}
          >
            {a === 'text' ? 'Fließtext' : 'Landkarte'}
          </button>
        ))}
      </div>

      {ansicht === 'text' ? (
        themen.map(t => (
          <p key={t.id} style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 12px' }}>
            <Absatz absatz={t.absatz} onKarte={onKarte} />
          </p>
        ))
      ) : (
        /*
         * LANDKARTE: dieselben Ziele, nur ohne Prosa — je Thema eine Zeile und die
         * Chips darunter. Sie kommen aus `chips[]`, NICHT aus dem Absatz: `warum`
         * ist ein Halbsatz ohne Marker, und durch den Absatz-Zerleger gejagt ergaebe
         * er eine Ansicht, in der nichts klickbar ist — eine Landkarte ohne Wege.
         */
        themen.map(t => (
          <div key={t.id} style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--tn-border)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</span>
              {t.fortschritt && (
                <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
                  {t.fortschritt.bereit} bereit
                  {t.fortschritt.in_arbeit > 0 ? ` · ${t.fortschritt.in_arbeit} in Arbeit` : ''}
                </span>
              )}
            </div>
            {t.warum && (
              <div style={{ fontSize: 13, color: 'var(--tn-text-muted)', marginTop: 1 }}>{t.warum}</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {(t.chips ?? []).map(c => {
                const bereit = c.zustand === 'bereit';
                return (
                  <button
                    key={c.kartenId}
                    data-ai-id={`guide-chip-${c.kartenId}`}
                    onClick={() => onKarte(c.kartenId)}
                    title={c.fehlt?.length ? `Fehlt noch: ${c.fehlt.join(' · ')}` : undefined}
                    style={{
                      padding: '2px 10px', fontSize: 13, borderRadius: 11, cursor: 'pointer',
                      textAlign: 'left',
                      background: bereit ? 'rgba(122,162,247,0.12)' : 'transparent',
                      color: bereit ? 'var(--tn-blue)' : 'var(--tn-text-muted)',
                      border: `1px solid ${bereit ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
                    }}
                  >
                    {c.beschriftung}
                    {!bereit && c.fehlt?.length ? (
                      <span style={{ opacity: 0.75 }}> — fehlt: {kurz(c.fehlt[0])}{c.fehlt.length > 1 ? ` +${c.fehlt.length - 1}` : ''}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Was heute fertig wurde. Nicht als Aufgabe, sondern als Beleg — und
          zusammengeklappt, weil zwanzig Zeilen Erledigtes den Blick auf das Offene
          verstellen. */}
      {daten.heute_geschafft?.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 13, color: 'var(--tn-text-muted)', cursor: 'pointer' }}>
            Heute geschafft: {daten.heute_geschafft.length}
          </summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.5, color: 'var(--tn-text-muted)' }}>
            {daten.heute_geschafft.map((z: string, i: number) => <li key={i}>{z}</li>)}
          </ul>
        </details>
      )}
      {!themen.length && (
        <div style={{ fontSize: 13, color: 'var(--tn-text-muted)' }}>
          Der Coach hat noch kein Thema beschrieben.
        </div>
      )}
    </div>
  );
}
